import React, { useState, useRef, useCallback, useEffect, forwardRef } from 'react';

function extractYouTubeId(url) {
  const match = url.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|v\/))([a-zA-Z0-9_-]{11})/);
  return match ? match[1] : null;
}

function formatTime(s) {
  if (!s || isNaN(s)) return '0:00';
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  return `${m}:${String(sec).padStart(2, '0')}`;
}

const VideoPlayer = forwardRef(function VideoPlayer({ onPlayerReady, onPlay, onPause, onTimeUpdate, videoSync, initialUrl, actions = [], roundMarkers = [], currentRound }, ref) {
  const [sourceType, setSourceType] = useState('youtube');
  const [urlInput, setUrlInput]     = useState('');
  const [videoId, setVideoId]       = useState('');
  const [localSrc, setLocalSrc]     = useState('');
  const [duration, setDuration]     = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [isDragOver, setIsDragOver] = useState(false);
  const [speed, setSpeed]           = useState(1);

  const videoElRef  = useRef(null);
  const ytPlayerRef = useRef(null);
  const ytDivRef    = useRef(null);
  const timeUpdateInterval = useRef(null);

  const SPEEDS = [0.25, 0.5, 0.75, 1, 1.5, 2];
  const FRAME  = 1 / 25; // 25fps

  // -- YouTube IFrame API --
  const loadYouTubeAPI = useCallback(() => {
    if (window.YT && window.YT.Player) return Promise.resolve();
    return new Promise((resolve) => {
      window.onYouTubeIframeAPIReady = resolve;
      if (!document.querySelector('script[src*="youtube.com/iframe_api"]')) {
        const s = document.createElement('script');
        s.src = 'https://www.youtube.com/iframe_api';
        document.head.appendChild(s);
      }
    });
  }, []);

  const createYTPlayer = useCallback(async (vid) => {
    await loadYouTubeAPI();
    if (ytPlayerRef.current) { ytPlayerRef.current.destroy(); ytPlayerRef.current = null; }
    if (!ytDivRef.current) return;
    ytPlayerRef.current = new window.YT.Player(ytDivRef.current, {
      videoId: vid,
      playerVars: { autoplay: 0, controls: 0, modestbranding: 1, rel: 0 },
      events: {
        onReady: (e) => {
          setDuration(e.target.getDuration());
          if (onPlayerReady) onPlayerReady(e.target);
          if (videoSync) videoSync.registerPlayer(e.target);
          // time polling
          clearInterval(timeUpdateInterval.current);
          timeUpdateInterval.current = setInterval(() => {
            if (ytPlayerRef.current?.getCurrentTime) {
              const t = ytPlayerRef.current.getCurrentTime();
              setCurrentTime(t);
              if (onTimeUpdate) onTimeUpdate(t);
            }
          }, 100);
        },
        onStateChange: (e) => {
          if (e.data === window.YT.PlayerState.PLAYING) { if (onPlay) onPlay(); }
          if (e.data === window.YT.PlayerState.PAUSED)  { if (onPause) onPause(); }
        },
      },
    });
  }, [loadYouTubeAPI, onPlayerReady, onPlay, onPause, onTimeUpdate, videoSync]);

  const handleLoadYouTube = useCallback(() => {
    const vid = extractYouTubeId(urlInput);
    if (!vid) { alert('URL YouTube non valido'); return; }
    setVideoId(vid);
    createYTPlayer(vid);
  }, [urlInput, createYTPlayer]);

  // Auto-load URL proveniente dal wizard MatchSetup
  useEffect(() => {
    if (!initialUrl) return;
    setUrlInput(initialUrl);
    const vid = extractYouTubeId(initialUrl);
    if (vid) {
      setSourceType('youtube');
      setVideoId(vid);
      // Leggero ritardo per assicurarsi che ytDivRef sia montato
      setTimeout(() => createYTPlayer(vid), 100);
    } else {
      setSourceType('url');
      setLocalSrc(initialUrl);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    return () => {
      clearInterval(timeUpdateInterval.current);
      if (ytPlayerRef.current) ytPlayerRef.current.destroy();
    };
  }, []);

  // Refs per evitare stale closure nel keyboard handler
  const currentTimeRef = useRef(currentTime);
  const sourceTypeRef  = useRef(sourceType);
  useEffect(() => { currentTimeRef.current = currentTime; });
  useEffect(() => { sourceTypeRef.current  = sourceType;  });

  // Keyboard shortcuts: , = -1 frame, . = +1 frame
  useEffect(() => {
    const handler = (e) => {
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;
      const dir = e.key === ',' ? -1 : e.key === '.' ? 1 : 0;
      if (!dir) return;
      e.preventDefault();
      const t = currentTimeRef.current + dir * (1 / 25);
      if (sourceTypeRef.current === 'youtube' && ytPlayerRef.current) {
        ytPlayerRef.current.seekTo(t, true);
        setCurrentTime(t);
      } else if (videoElRef.current) {
        videoElRef.current.currentTime = t;
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []); // stabile, non dipende da state

  // -- HTML5 video handlers --
  const handleVideoPlay = () => { if (onPlay) onPlay(); };
  const handleVideoPause = () => { if (onPause) onPause(); };
  const handleTimeUpdate = () => {
    const v = videoElRef.current;
    if (!v) return;
    setCurrentTime(v.currentTime);
    if (onTimeUpdate) onTimeUpdate(v.currentTime);
  };
  const handleMetadata = () => {
    if (videoElRef.current) {
      setDuration(videoElRef.current.duration);
      if (onPlayerReady) onPlayerReady(videoElRef.current);
      if (videoSync) videoSync.registerPlayer(videoElRef.current);
    }
  };

  // -- File drop --
  const handleFileDrop = useCallback((e) => {
    e.preventDefault();
    setIsDragOver(false);
    const file = e.dataTransfer?.files?.[0] || e.target?.files?.[0];
    if (file && file.type.startsWith('video/')) {
      const url = URL.createObjectURL(file);
      setLocalSrc(url);
    }
  }, []);

  const handleFileInput = useCallback((e) => {
    const file = e.target.files[0];
    if (file) {
      const url = URL.createObjectURL(file);
      setLocalSrc(url);
    }
  }, []);

  // -- Common controls --
  const play = () => {
    if (sourceType === 'youtube' && ytPlayerRef.current) ytPlayerRef.current.playVideo();
    else if (videoElRef.current) videoElRef.current.play();
  };

  const pause = () => {
    if (sourceType === 'youtube' && ytPlayerRef.current) ytPlayerRef.current.pauseVideo();
    else if (videoElRef.current) videoElRef.current.pause();
  };

  const seek = (t) => {
    if (sourceType === 'youtube' && ytPlayerRef.current) ytPlayerRef.current.seekTo(t, true);
    else if (videoElRef.current) videoElRef.current.currentTime = t;
    setCurrentTime(t);
  };

  const frame = (dir) => { seek(currentTime + dir * FRAME); };

  const setSpeedFn = (s) => {
    setSpeed(s);
    if (sourceType === 'youtube' && ytPlayerRef.current) ytPlayerRef.current.setPlaybackRate(s);
    else if (videoElRef.current) videoElRef.current.playbackRate = s;
  };

  // ── Zoom timeline sul round corrente ──────────────────────────────────────
  const roundStart = roundMarkers.find(m => m.round === currentRound && m.type === 'start');
  const roundEnd   = roundMarkers.find(m => m.round === currentRound && m.type === 'end');
  const isZoomed   = !!(roundStart && roundEnd && duration > 0);
  // viewStart/viewEnd usati SOLO se entrambi i marker esistono, altrimenti video intero
  const viewStart  = isZoomed ? roundStart.timestamp_video : 0;
  const viewEnd    = isZoomed ? roundEnd.timestamp_video   : duration;
  const viewRange  = (viewEnd - viewStart) || duration || 1;

  const timeToPct = (t) => Math.min(100, Math.max(0, ((t - viewStart) / viewRange) * 100));
  const progressPct = duration > 0 ? timeToPct(currentTime) : 0;

  const handleProgressClick = (e) => {
    if (!duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const ratio = (e.clientX - rect.left) / rect.width;
    seek(viewStart + ratio * viewRange);
  };

  return (
    <div className="video-player">
      {/* Source type selector */}
      <div className="video-source-selector">
        {['youtube', 'url', 'file'].map(t => (
          <button
            key={t}
            className={`btn btn-sm ${sourceType === t ? 'btn-primary' : 'btn-ghost'}`}
            onClick={() => setSourceType(t)}
          >
            {t === 'youtube' ? 'YouTube' : t === 'url' ? 'URL/NAS' : 'File Locale'}
          </button>
        ))}
      </div>

      {/* URL input row */}
      {(sourceType === 'youtube' || sourceType === 'url') && (
        <div className="url-input-row">
          <input
            className="url-input"
            placeholder={sourceType === 'youtube' ? 'URL YouTube (es. https://youtu.be/xxx)' : 'URL video (http://... o /path/...)'}
            value={urlInput}
            onChange={e => setUrlInput(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && (sourceType === 'youtube' ? handleLoadYouTube() : setLocalSrc(urlInput))}
          />
          <button
            className="btn btn-sm btn-primary"
            onClick={sourceType === 'youtube' ? handleLoadYouTube : () => setLocalSrc(urlInput)}
          >
            Carica
          </button>
        </div>
      )}

      {/* Video container */}
      <div className="video-container">
        {/* YouTube */}
        {sourceType === 'youtube' && (
          <>
            {!videoId && (
              <div style={{ color: 'var(--color-text-dim)', fontSize: 13, textAlign: 'center' }}>
                Inserisci un URL YouTube e premi Carica
              </div>
            )}
            <div style={{ position: 'relative', width: '100%', height: '100%', display: videoId ? 'block' : 'none' }}>
              <div
                ref={ytDivRef}
                id="yt-player"
                style={{ width: '100%', height: '100%' }}
              />
              {/* Overlay trasparente: impedisce all'iframe YouTube di catturare il focus
                  così le shortcut tastiera continuano a funzionare ovunque nella pagina */}
              <div style={{ position: 'absolute', inset: 0, zIndex: 1 }} />
            </div>
          </>
        )}

        {/* URL / NAS */}
        {sourceType === 'url' && (
          <>
            {!localSrc && (
              <div style={{ color: 'var(--color-text-dim)', fontSize: 13, textAlign: 'center' }}>
                Inserisci URL video e premi Carica
              </div>
            )}
            {localSrc && (
              <video
                ref={videoElRef}
                src={localSrc}
                style={{ width: '100%', height: '100%' }}
                onPlay={handleVideoPlay}
                onPause={handleVideoPause}
                onTimeUpdate={handleTimeUpdate}
                onLoadedMetadata={handleMetadata}
              />
            )}
          </>
        )}

        {/* File locale */}
        {sourceType === 'file' && (
          <>
            {!localSrc ? (
              <div
                className={`drop-zone${isDragOver ? ' drag-over' : ''}`}
                onDragOver={e => { e.preventDefault(); setIsDragOver(true); }}
                onDragLeave={() => setIsDragOver(false)}
                onDrop={handleFileDrop}
                onClick={() => document.getElementById('file-input-hidden').click()}
              >
                <span className="drop-zone-icon">&#127909;</span>
                <span>Trascina qui il file video</span>
                <span style={{ fontSize: 12 }}>oppure clicca per selezionare</span>
                <input
                  id="file-input-hidden"
                  type="file"
                  accept="video/*"
                  style={{ display: 'none' }}
                  onChange={handleFileInput}
                />
              </div>
            ) : (
              <video
                ref={videoElRef}
                src={localSrc}
                style={{ width: '100%', height: '100%' }}
                onPlay={handleVideoPlay}
                onPause={handleVideoPause}
                onTimeUpdate={handleTimeUpdate}
                onLoadedMetadata={handleMetadata}
              />
            )}
          </>
        )}
      </div>

      {/* Controls */}
      <div className="video-controls">
        {/* Label zoom round */}
        {isZoomed && (
          <div style={{ fontSize: 10, color: 'var(--color-orange)', textAlign: 'center', marginBottom: 2, letterSpacing: 0.5 }}>
            ⬛ R{currentRound} · {formatTime(viewStart)} → {formatTime(viewEnd)}
          </div>
        )}

        {/* Progress bar con markers azioni */}
        <div className="video-progress" onClick={handleProgressClick} style={{ position: 'relative' }}>
          <div className="video-progress-bar" style={{ width: `${progressPct}%` }} />

          {/* Round markers — visibili solo se nel range corrente */}
          {duration > 0 && roundMarkers
            .filter(m => m.timestamp_video != null && m.timestamp_video >= viewStart && m.timestamp_video <= viewEnd)
            .map((m) => {
              const pct   = timeToPct(m.timestamp_video);
              const color = m.type === 'start' ? 'var(--color-green)' : 'var(--color-orange)';
              return (
                <div
                  key={`rm-${m.id}`}
                  onClick={(e) => { e.stopPropagation(); seek(m.timestamp_video); }}
                  title={`R${m.round} ${m.type === 'start' ? 'Inizio' : 'Fine'}: ${m.timestamp_display}`}
                  style={{
                    position: 'absolute', left: `${pct}%`, top: 0, bottom: 0,
                    width: 3, background: color, opacity: 1,
                    transform: 'translateX(-50%)', cursor: 'pointer', borderRadius: 2,
                    boxShadow: `0 0 6px ${color}, 0 0 2px ${color}`,
                  }}
                />
              );
            })}

          {/* Azioni — visibili solo se nel range corrente */}
          {duration > 0 && actions
            .filter(a => a.timestamp_video != null && a.timestamp_video >= viewStart && a.timestamp_video <= viewEnd)
            .map((a) => {
              const pct    = timeToPct(a.timestamp_video);
              const scored = a.outcome === 'punto';
              const color  = a.athlete === 'blue' ? 'var(--color-blue)' : 'var(--color-red)';
              return (
                <div
                  key={a.id}
                  title={`${a.athlete === 'blue' ? 'BLU' : 'ROSSO'} — ${a.technique}${scored ? ` +${a.points}pt` : ''} @ ${formatTime(a.timestamp_video)}`}
                  onClick={(e) => { e.stopPropagation(); seek(a.timestamp_video); }}
                  style={{
                    position: 'absolute',
                    left:     `${pct}%`,
                    top:      0,
                    bottom:   0,
                    width:    scored ? 4 : 3,
                    background: color,
                    opacity:  scored ? 1 : 0.65,
                    transform: 'translateX(-50%)',
                    cursor:   'pointer',
                    borderRadius: 2,
                    boxShadow: scored ? `0 0 5px ${color}` : 'none',
                  }}
                />
              );
            })}
        </div>

        <div className="video-controls-row">
          {/* Play/Pause */}
          <button className="btn btn-sm btn-primary" onClick={play} title="Play">&#9654;</button>
          <button className="btn btn-sm btn-secondary" onClick={pause} title="Pausa">&#9646;&#9646;</button>

          {/* Frame stepping */}
          <button className="btn btn-sm btn-ghost" onClick={() => frame(-1)} title="-1 frame">&#9668;</button>
          <button className="btn btn-sm btn-ghost" onClick={() => frame(1)} title="+1 frame">&#9658;</button>

          {/* Time */}
          <span className="video-time">{formatTime(currentTime)} / {formatTime(duration)}</span>

          {/* Speed */}
          <div style={{ marginLeft: 'auto', display: 'flex', gap: 2 }}>
            {SPEEDS.map(s => (
              <button
                key={s}
                className={`btn btn-sm ${speed === s ? 'btn-warning' : 'btn-ghost'}`}
                onClick={() => setSpeedFn(s)}
                style={{ minWidth: 36, fontSize: 11, padding: '3px 4px' }}
              >
                {s}x
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
});

export default VideoPlayer;
